import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ArrowLeft, Check, ShoppingCart } from 'lucide-react';
import { CartItem, Product, ProductVariant, SiteTheme } from '../../types';
import { SEOHead } from '../SEOHead';
import { db } from '../../services/db';
import { DEFAULT_SHOP_CONTENT } from '../../services/db/shopContent';
import { applyChoices, BundleChoices, bundleItemsFor, bundleValue, choiceProducts, choicesComplete, isBundle, resolveBundle } from '../../services/bundles';
import { bundlesContaining, cardSubtitle, effectiveStock, isPurchasable, relatedFor, reviewsFor } from '../../services/shop';
import { formatKr } from '../../services/format';
import { VariantPicker } from './VariantPicker';
import { BundleContents, StoreBundleInfo } from './BundleContents';
import { CampaignCountdown } from './CampaignCountdown';
import { MediaTicker, ProductGallery, ProductInfo, RelatedRow, ReviewsSheet, ReviewTicker, StorySections } from './coffeenova';
import { toCardProduct, toMediaItems, toReview, toStockStatus, toStories } from './coffeenova/adapters';
import { StoreImage } from '../ui/StoreImage';

/** Butikens färger (samma som i Jonathans coffeenova.css). */
const SHOP_GREEN = '#4A5846';

/** Valen (t.ex. Färg) och deras alternativ, i varianternas ordning. */
const attributesOf = (variants?: ProductVariant[]): Record<string, string[]> => {
    const map: Record<string, string[]> = {};
    (variants || []).forEach(v => Object.entries(v.attributes || {}).forEach(([k, val]) => {
        map[k] = map[k] || [];
        if (!map[k].includes(val)) map[k].push(val);
    }));
    return map;
};
const SHOP_PINK = '#E1B9BC';

interface StoreProductPageProps {
    product: Product;
    onBack: () => void;
    onAddToCart: (product: Product, variant?: ProductVariant, openDrawer?: boolean, choices?: BundleChoices) => void;
    cart: CartItem[];
    onOpenCart: () => void;
    /** Produkterna i gruppens butik. */
    allProducts?: Product[];
    /** Alla produkter, för paketens innehåll (också sådana som inte visas i butiken). */
    catalog?: Product[];
    onSelectRelated?: (product: Product, variantId?: string, choices?: BundleChoices) => void;
    /** Varianten som är vald när sidan öppnas. */
    initialVariantId?: string | null;
    /** Paket: valen när sidan öppnas, t.ex. samma färg på Flaskan som kunden just valde. */
    initialChoices?: BundleChoices;
    theme?: SiteTheme;
    /** Gruppens slutdatum, för nedräkningen. */
    campaignEnd?: number;
}

/**
 * Produktsidan, mobil först (Jonathans design, components/store/coffeenova).
 * Överst: galleri, namn, pris, citat, ansikten + stjärnor + länk till
 * recensionsvyn, lagerstatus, beskrivning med "Läs mer", punkter, val,
 * paketets innehåll, köpknappen och paket med produkten. Köpknappen ligger
 * kvar längst ned på mobil. Vid scroll: "Du kanske också gillar" på en rad,
 * "Vad kunderna säger" och "Från våra kunder" som rullande rader, Kaffet och Om oss.
 */
export const StoreProductPage: React.FC<StoreProductPageProps> = ({
    product, onBack, onAddToCart, cart, onOpenCart, allProducts = [], catalog, onSelectRelated, campaignEnd, initialVariantId, initialChoices,
}) => {
    const all = catalog || allProducts;
    const hasVariants = !!product.variants && product.variants.length > 0;
    const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
    const [added, setAdded] = useState(false);
    const [reviewsOpen, setReviewsOpen] = useState(false);
    const { data: content = DEFAULT_SHOP_CONTENT } = useSWR('shop-content', () => db.getShopContent(), { revalidateOnFocus: false });

    // Valen (t.ex. Malning) och deras alternativ, i varianternas ordning.
    const attributeMap = useMemo(() => attributesOf(product.variants), [product]);

    useEffect(() => {
        const defaults: Record<string, string> = {};
        Object.entries(attributeMap).forEach(([k, vals]) => { defaults[k] = vals[0]; });
        const initial = initialVariantId ? product.variants?.find(v => v.id === initialVariantId) : undefined;
        setSelectedAttributes({ ...defaults, ...(initial?.attributes || {}) });
        setAdded(false);
        setReviewsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- förvalet gäller bara när sidan öppnas
    }, [product.id, attributeMap]);

    const selectedVariant = useMemo(() => {
        if (!hasVariants) return undefined;
        const withAttrs = product.variants!.find(v => Object.entries(selectedAttributes).every(([k, val]) => v.attributes?.[k] === val));
        // Varianter utan val (t.ex. ett enda alternativ) väljs direkt.
        return withAttrs || (Object.keys(attributeMap).length === 0 ? product.variants![0] : undefined);
    }, [product, selectedAttributes, hasVariants, attributeMap]);

    const price = selectedVariant ? selectedVariant.price : product.price;

    // Paket där varianterna bara skiljer sig i en del (t.ex. färgen på Flaskan):
    // rubriken säger vilken del valet gäller, "Färg på Flaskan".
    const choicePart = useMemo(() => {
        const vs = (product.variants || []).filter(v => (v.bundleItems?.length ?? 0) > 0);
        if (vs.length < 2) return null;
        const ids = new Set(vs.flatMap(v => v.bundleItems!.map(b => b.productId)));
        const varying = [...ids].filter(id => new Set(vs.map(v => v.bundleItems!.find(b => b.productId === id)?.variantId ?? '')).size > 1);
        return varying.length === 1 ? all.find(p => p.id === varying[0])?.name ?? null : null;
    }, [product, all]);
    const choiceLabel = (key: string) => (choicePart ? `${key} på ${choicePart.split(' - ')[0]}` : undefined);

    // Paket: delar där kunden väljer variant (t.ex. färgen på Flaskan), med
    // samma väljare som på delens egen sida. Första alternativet är förvalt.
    const bundleItems = useMemo(() => (isBundle(product)
        ? bundleItemsFor(product, selectedVariant || product.variants?.find(v => (v.bundleItems?.length ?? 0) > 0))
        : []), [product, selectedVariant]);
    const choiceParts = useMemo(() => choiceProducts(bundleItems, all), [bundleItems, all]);
    const [partAttrs, setPartAttrs] = useState<Record<string, Record<string, string>>>({});
    useEffect(() => {
        const next: Record<string, Record<string, string>> = {};
        choiceParts.forEach(p => {
            const start = initialChoices?.[p.id] ? p.variants?.find(v => v.id === initialChoices[p.id]) : undefined;
            const defaults = Object.fromEntries(Object.entries(attributesOf(p.variants)).map(([k, vals]) => [k, vals[0]]));
            next[p.id] = { ...defaults, ...(start?.attributes || {}) };
        });
        setPartAttrs(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- förvalet gäller när sidan eller innehållet byts
    }, [product.id, choiceParts.map(p => p.id).join(',')]);
    const choices = useMemo((): BundleChoices | undefined => {
        if (choiceParts.length === 0) return undefined;
        const out: BundleChoices = {};
        choiceParts.forEach(p => {
            const attrs = partAttrs[p.id] || {};
            const keys = Object.keys(attributesOf(p.variants));
            const v = keys.length === 0 ? p.variants![0] : p.variants!.find(x => keys.every(k => x.attributes?.[k] === attrs[k]));
            if (v) out[p.id] = v.id;
        });
        return out;
    }, [choiceParts, partAttrs]);

    const bundle = useMemo((): StoreBundleInfo | null => {
        if (!isBundle(product)) return null;
        const parts = resolveBundle(applyChoices(bundleItems, choices), all, []);
        if (parts.length === 0 || parts.some(p => p.missing)) return null;
        const value = bundleValue(parts);
        return { parts, value, savings: Math.round((value - price) * 100) / 100 };
    }, [product, bundleItems, choices, all, price]);

    const images = useMemo(() => {
        const v = selectedVariant;
        const own = v?.images?.length ? v.images : v?.imageUrl ? [v.imageUrl] : product.images?.length ? product.images : product.imageUrl ? [product.imageUrl] : [];
        if (own.length > 0 || !bundle) return own;
        return bundle.parts.map(p => p.imageUrl).filter((u): u is string => !!u);
    }, [product, selectedVariant, bundle]);

    const stock = effectiveStock(product, all);
    const soldOut = !isPurchasable(product, all);
    const ready = (!hasVariants || !!selectedVariant) && !soldOut && choicesComplete(bundleItems, all, choices);
    const subtitle = cardSubtitle(product, all);
    // Citatet, ansiktena, stjärnorna och recensionsvyn gäller den här produkten.
    // De rullande raderna visar alla publicerade recensioner i butiken.
    const productReviews = useMemo(() => reviewsFor(product.id, content.reviews).list.map(toReview), [product.id, content.reviews]);
    const allReviews = useMemo(() => content.reviews.filter(r => r.published).map(toReview), [content.reviews]);
    const freeMedia = useMemo(() => toMediaItems(content.media), [content.media]);
    const stories = useMemo(() => toStories(content), [content]);
    const upsell = useMemo(() => bundlesContaining(product, allProducts), [product, allProducts]);
    const related = useMemo(() => relatedFor(product, allProducts), [product, allProducts]);
    const relatedCards = useMemo(() => {
        const base = window.location.hash.split('?')[0] || '#/';
        return related.map(p => toCardProduct(p, `${base}?product=${encodeURIComponent(p.id)}`));
    }, [related]);
    const cartCount = cart.reduce((s, l) => s + l.quantity, 0);
    const savings = bundle && bundle.savings > 0 ? bundle.savings : 0;

    const buy = () => {
        if (!ready) return;
        onAddToCart(product, selectedVariant, true, choices);
        setAdded(true);
    };

    const buyLabel = soldOut ? 'Slut i lager' : !ready ? 'Välj ett alternativ' : `Lägg i varukorgen · ${formatKr(price)}`;
    const BuyButton = ({ className = '' }: { className?: string }) => (
        <button type="button" onClick={buy} disabled={!ready}
            className={`w-full min-h-[52px] px-5 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 ${className}`}
            style={{ backgroundColor: soldOut ? '#57534e' : SHOP_GREEN }}>
            {added && !soldOut ? <Check size={18} /> : <ShoppingCart size={18} />}
            {buyLabel}
        </button>
    );

    return (
        <div className="cn-page">
            <SEOHead
                title={`${product.name} | CoffeeNova`}
                description={product.description}
                imageUrl={images[0] || product.imageUrl}
                type="product"
                productData={{ name: product.name, description: product.description, price, currency: 'SEK', imageUrl: images[0] || product.imageUrl }}
            />

            {/* Överst: tillbaka till butiken och varukorgen. */}
            <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200">
                <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
                    <button type="button" onClick={onBack} className="inline-flex items-center gap-2 min-h-[44px] pr-3 text-sm font-bold text-stone-800">
                        <ArrowLeft size={18} /> Butiken
                    </button>
                    <button type="button" onClick={onOpenCart} aria-label={`Varukorg, ${cartCount} ${cartCount === 1 ? 'produkt' : 'produkter'}`}
                        className="relative w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: SHOP_GREEN }}>
                        <ShoppingCart size={18} />
                        {cartCount > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[11px] font-black flex items-center justify-center border-2 border-white">{cartCount}</span>}
                    </button>
                </div>
            </div>

            {soldOut && (
                <div role="status" className="bg-stone-900 text-white text-center text-sm font-bold py-2.5 px-4">
                    Slut i lager. Den går inte att beställa just nu.
                </div>
            )}

            {/* Primära delen */}
            <div className="max-w-6xl mx-auto md:px-8 md:pt-10 pb-10 md:grid md:grid-cols-2 md:gap-12 md:items-start">
                {/* Mobil: galleriet går kant i kant. Dator: rundade hörn, ligger kvar vid scroll. */}
                <div className={`md:sticky md:top-20 md:rounded-3xl md:overflow-hidden ${soldOut ? 'opacity-60 grayscale' : ''}`}>
                    <ProductGallery key={images.join('|')} images={images} alt={product.name} />
                </div>

                <div className="px-4 md:px-0">
                    <ProductInfo
                        name={product.name}
                        price={price}
                        description={product.description || ''}
                        stockStatus={toStockStatus(stock)}
                        reviews={productReviews}
                        onOpenReviews={() => setReviewsOpen(true)}
                        eyebrow={product.seasonTag ? (
                            <span className="self-start px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wide text-stone-900" style={{ backgroundColor: SHOP_PINK }}>
                                {product.seasonTag}
                            </span>
                        ) : undefined}
                        subtitle={subtitle}
                        priceNote={savings > 0 ? (
                            <span className="text-sm text-stone-500">
                                <span className="line-through">{formatKr(price + savings)}</span> var för sig · <span className="font-bold" style={{ color: SHOP_GREEN }}>du sparar {formatKr(savings)}</span>
                            </span>
                        ) : undefined}
                        afterStock={<CampaignCountdown campaignEnd={campaignEnd} className="text-sm text-stone-600" />}
                    />

                    {(product.highlights?.length ?? 0) > 0 && (
                        <ul className="mt-5 space-y-2 list-none p-0">
                            {product.highlights!.map((h, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-base text-stone-800">
                                    <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: SHOP_GREEN }} aria-hidden="true" />{h}
                                </li>
                            ))}
                        </ul>
                    )}

                    {Object.entries(attributeMap).map(([key, values]) => (
                        <VariantPicker
                            key={key}
                            name={key}
                            label={choiceLabel(key)}
                            values={values}
                            selected={selectedAttributes[key]}
                            onSelect={val => setSelectedAttributes(prev => ({ ...prev, [key]: val }))}
                            accent={SHOP_GREEN}
                        />
                    ))}

                    {choiceParts.map(part => Object.entries(attributesOf(part.variants)).map(([key, values]) => (
                        <VariantPicker
                            key={`${part.id}-${key}`}
                            name={key}
                            label={`${key} på ${part.name.split(' - ')[0]}`}
                            values={values}
                            selected={partAttrs[part.id]?.[key]}
                            onSelect={val => setPartAttrs(prev => ({ ...prev, [part.id]: { ...(prev[part.id] || {}), [key]: val } }))}
                            accent={SHOP_GREEN}
                        />
                    )))}

                    {bundle && <BundleContents bundle={bundle} className="mt-6" />}

                    {/* Köpknappen. På mobil ligger den också fast längst ned. */}
                    <div className="mt-6 hidden md:block"><BuyButton /></div>

                    {/* Paket med produkten, med jämförpris (delarnas priser). */}
                    {upsell.length > 0 && (
                        <div className="mt-6 rounded-3xl p-4 md:p-5" style={{ backgroundColor: `${SHOP_PINK}55` }}>
                            <h2 className="text-base font-bold text-primary mb-3">Spara med ett paket</h2>
                            <ul className="space-y-3 list-none p-0">
                                {upsell.map(b => {
                                    // Samma val som här följer med till paketet (t.ex. Flaskan i Pastel Pink).
                                    const same = selectedVariant ? b.variants?.find(v => v.bundleItems?.some(i => i.productId === product.id && i.variantId === selectedVariant.id)) : undefined;
                                    const variant = same || b.variants?.find(v => (v.bundleItems?.length ?? 0) > 0);
                                    const bItems = bundleItemsFor(b, variant);
                                    const myChoice: BundleChoices | undefined = selectedVariant && bItems.some(i => i.productId === product.id && !i.variantId) ? { [product.id]: selectedVariant.id } : undefined;
                                    const parts = resolveBundle(applyChoices(bItems, myChoice), all, []);
                                    const bPrice = variant ? variant.price : b.price;
                                    const bValue = bundleValue(parts);
                                    const img = b.images?.[0] || b.imageUrl || parts.find(p => p.imageUrl)?.imageUrl;
                                    const needsChoice = (!same && (b.variants?.length ?? 0) > 1) || !choicesComplete(bItems, all, myChoice);
                                    const chosenName = same?.name || (myChoice ? selectedVariant!.name : '');
                                    return (
                                        <li key={b.id} className="flex items-center gap-3 bg-white rounded-2xl p-3">
                                            <button type="button" onClick={() => onSelectRelated?.(b, same?.id, myChoice)} className="w-16 h-16 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0" aria-label={`Visa ${b.name}`}>
                                                {img && <StoreImage src={img} width={256} alt="" className="w-full h-full object-cover" loading="lazy" />}
                                            </button>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-stone-900 leading-tight">{b.name}</p>
                                                <p className="text-xs text-stone-500 truncate">{chosenName ? `${chosenName} · ` : ''}{cardSubtitle(b, all)}</p>
                                                <p className="text-sm mt-0.5">
                                                    <span className="font-bold text-stone-900">{formatKr(bPrice)}</span>
                                                    {bValue > bPrice && <span className="text-xs text-stone-500 ml-1.5"><span className="line-through">{formatKr(bValue)}</span> var för sig</span>}
                                                </p>
                                            </div>
                                            <button type="button"
                                                onClick={() => (needsChoice ? onSelectRelated?.(b, same?.id, myChoice) : onAddToCart(b, variant, true, myChoice))}
                                                aria-label={needsChoice ? `Välj ${b.name}` : `Lägg ${b.name}${chosenName ? ` (${chosenName})` : ''} i varukorgen`}
                                                className="flex-shrink-0 min-h-[40px] px-3.5 rounded-xl border-2 text-sm font-bold"
                                                style={{ borderColor: SHOP_GREEN, color: SHOP_GREEN }}>
                                                {needsChoice ? 'Välj' : 'Lägg till'}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* När man scrollar vidare */}
            <div className="max-w-6xl mx-auto">
                <RelatedRow
                    products={relatedCards}
                    onSelect={c => { const p = related.find(x => x.id === c.id); if (p) onSelectRelated?.(p); }}
                />
            </div>
            <ReviewTicker reviews={allReviews} />
            <MediaTicker reviews={allReviews} extra={freeMedia} />
            <StorySections sections={stories} />
            <div className="h-12 md:h-20" aria-hidden="true" />

            <ReviewsSheet open={reviewsOpen} onClose={() => setReviewsOpen(false)} reviews={productReviews} />

            {/* Mobil: köpknappen ligger kvar längst ned. */}
            <div className="md:hidden h-24" aria-hidden="true" />
            <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
                <BuyButton />
            </div>
        </div>
    );
};
